import React, { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import ReactDatePicker from "../../common/ReactDatePicker";
import { formtDt } from "../../util/AppUtils";
import { createProcedureRevisionMOS, getProcedureRevisionsForPart } from "../../redux/procedureRevisionSlice";
import { showSnackbar } from "../../redux/snackbarSlice";

const RevisionTable = ({
  revisionData,
  procedureNumber,
  procedureType,
  partId,
  onRevisionCreated,
  onEditClick,
  revisionsForPart,
  parseRevision,
  sortRevisions,
}) => {
  const dispatch = useDispatch();
  
  const [newRows, setNewRows] = useState([]);

  // Memoized revision table rows
  const revisionTableRows = useMemo(() => {
    return sortRevisions(revisionData).map((rev, index) => {
      if (!procedureNumber) return null;

      const requiredBy = "required_by" in rev ? rev.required_by : [];

      const submitter = requiredBy.find(
        (rb) => rb.purpose?.toLowerCase() === "submit"
      )?.user;

      const releaser = requiredBy.find(
        (rb) => rb.purpose?.toLowerCase() === "release"
      )?.user;

      const approvers = requiredBy
        .filter((rb) => rb.purpose?.toLowerCase() === "approve")
        .map((rb) => rb.user)
        .filter(Boolean);

      const formatNameLocal = (user) =>
        user ? `${user.first_name} ${user.last_name}` : "";

      const isMaintainedOutside =
        rev.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase() ===
        "maintained outside of system";

      return (
        <tr key={`existing-${index}`}>
          <td>{rev.rev || "-"}</td>
          <td>{formtDt(rev.date_released)}</td>
          <td>{rev.revision_details || "-"}</td>
          <td>
            {isMaintainedOutside
              ? "MAINTAINED OUTSIDE OF SYSTEM"
              : formatNameLocal(submitter) || rev.submitter}
          </td>
          <td>
            {isMaintainedOutside
              ? "MAINTAINED OUTSIDE OF SYSTEM"
              : approvers.length > 0
              ? approvers.map(formatNameLocal).join(", ")
              : rev.approver}
          </td>
          <td>
            {isMaintainedOutside
              ? "MAINTAINED OUTSIDE OF SYSTEM"
              : formatNameLocal(releaser) || rev.releasedBy}
          </td>
          <td>{rev.reference || "-"}</td>
          <td>
            {rev.status_changes?.find((sc) => sc.is_latest)
              ?.status_changed_to || rev.status}
          </td>
          <td>
            {!isMaintainedOutside && (
              <button
                className="right-arrow"
                onClick={async () => {
                  onEditClick(
                    rev.procedure_revision_id,
                    rev.rev,
                    rev.status_changes?.find((sc) => sc.is_latest)
                      ?.status_changed_to || "-",
                    rev.part_ref,
                    revisionsForPart?.type_data?.name
                  );
                }}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            )}
          </td>
        </tr>
      );
    }).filter(Boolean);
  }, [revisionData, procedureNumber, revisionsForPart?.type_data?.name, sortRevisions, onEditClick]);

  // Memoized new rows table
  const newRowsTable = useMemo(() => {
    return newRows.map((row, index) => (
      <tr key={`new-${index}`}>
        <td>
          <input
            value={row.rev}
            onChange={(e) =>
              handleNewRowChange(index, "rev", e.target.value)
            }
          />
        </td>
        <td>
          <ReactDatePicker
            selectedDate={row.date_released}
            onChange={(date) => {
              handleNewRowChange(index, "date_released", date);
            }}
          />
        </td>
        <td>
          <input
            value={row.revision_details}
            onChange={(e) =>
              handleNewRowChange(
                index,
                "revision_details",
                e.target.value
              )
            }
          />
        </td>
        <td>
          <input
            value={row.submitter}
            onChange={(e) =>
              handleNewRowChange(index, "submitter", e.target.value)
            }
            disabled
          />
        </td>
        <td>
          <input
            value={row.approver}
            onChange={(e) =>
              handleNewRowChange(index, "approver", e.target.value)
            }
            disabled
          />
        </td>
        <td>
          <input
            value={row.releasedBy}
            onChange={(e) =>
              handleNewRowChange(index, "releasedBy", e.target.value)
            }
            disabled
          />
        </td>
        <td>
          <input
            value={row.reference}
            onChange={(e) =>
              handleNewRowChange(index, "reference", e.target.value)
            }
          />
        </td>
        <td>
          <input
            value={row.status}
            onChange={(e) =>
              handleNewRowChange(index, "status", e.target.value)
            }
            disabled
          />
        </td>
        <td>
          <div className="action-buttons">
            <button
              className="save-row"
              onClick={() => handleSaveNewRow(index, row)}
            >
              ✔
            </button>
            <button
              className="cancel-row"
              onClick={() => handleDeleteNewRow(index)}
            >
              ✕
            </button>
          </div>
        </td>
      </tr>
    ));
  }, [newRows]);

  // Helper function to format date for input
  const formatDateForInput = useCallback((date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // Handle new row field changes
  const handleNewRowChange = useCallback((index, field, value) => {
    const updated = [...newRows];
    updated[index][field] = value;
    setNewRows(updated);
  }, [newRows]);

  // Handle delete new row
  const handleDeleteNewRow = useCallback((index) => {
    setNewRows(newRows.filter((_, i) => i !== index));
  }, [newRows]);

  // Handle save new row
  const handleSaveNewRow = useCallback(async (index, row) => {
    if (!procedureNumber || procedureNumber.length === 0) {
      dispatch(showSnackbar({ type: "error", message: "Please select a procedure number to proceed." }));
      return;
    }
    
    setNewRows(newRows.filter((_, i) => i !== index));

    const revisionData = {
      rev: row.rev,
      revision_details: row.revision_details,
      part_ref: partId,
      is_latest: true,
      is_maintained_outside_system: true,
      status_changed_to: "MAINTAINED OUTSIDE OF SYSTEM",
      is_in_release_history: false,
      past_procedure_revisions_reviewed: {},
      required_by_details: [],
      reference: row.reference,
      date_released: row.date_released,
    };

    try {
      const payload = await dispatch(
        createProcedureRevisionMOS({
          revisionData: revisionData,
          version_content: {
            action_to_show: `Created ${procedureNumber} Rev ${row.rev}`,
            current_status: "MAINTAINED OUTSIDE OF SYSTEM",
            procedure_number: procedureNumber,
            type: procedureType,
            description: "",
            requested_by: localStorage.getItem("user_name"),
            ...revisionData,
          },
        })
      ).unwrap();

      const createdRevisionId =
        payload?.results?.tableOfProcedures?.procedure_revision_ref;

      if (!createdRevisionId) {
        throw new Error("Missing revision ID in response.");
      }

      dispatch(showSnackbar({ type: "success", message: "Revision created successfully!" }));
      
      if (onRevisionCreated) {
        onRevisionCreated(createdRevisionId);
      }

      // Refresh the revisions data
      await dispatch(getProcedureRevisionsForPart({ partId }));
      
    } catch (error) {
      dispatch(showSnackbar({ type: "error", message: "Error creating revision: " + error }));
    }
  }, [procedureNumber, partId, procedureType, newRows, dispatch, onRevisionCreated]);

  // Handle add new row
  const handleAddNewRow = useCallback(() => {
    if (newRows.length === 0) {
      setNewRows([
        {
          rev: "",
          date_released: new Date(), // Use Date object instead of formatted string
          revision_details: "",
          submitter: "MAINTAINED OUTSIDE OF SYSTEM",
          approver: "MAINTAINED OUTSIDE OF SYSTEM",
          releasedBy: "MAINTAINED OUTSIDE OF SYSTEM",
          reference: "",
          status: "MAINTAINED OUTSIDE OF SYSTEM",
        },
      ]);
    }
  }, [newRows.length]);

  return (
    <>
      {/* Revisions Table */}
      <h3 className="table-heading">Revisions Table</h3>
      
      <table className="table">
        <thead>
          <tr>
            <th>Rev</th>
            <th>Date Released</th>
            <th>Revision Details</th>
            <th>Submitter</th>
            <th>Approvers</th>
            <th>Released By</th>
            <th>References</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {revisionTableRows}
          {newRowsTable}
        </tbody>
      </table>

      {/* Add Row Button */}
      <button
        onClick={handleAddNewRow}
        className="add-row-button"
      >
        + Add Row
      </button>
    </>
  );
};

export default RevisionTable;
